/**
 * EDITOR-ONLY Swiper behavior for the Slider widget's live preview — runs
 * inside the editor's preview iframe (same context as elementor-slider.js),
 * but deliberately kept in its own file so a bug here can never reach real
 * visitors (see the Elementor migration plan, Phase 2, slider widget
 * rebuild). Hooks the SAME `frontend/element_ready/gws-slider.default`
 * action as elementor-slider.js — Elementor supports multiple independent
 * listeners on one hook name — but early-returns unless
 * elementorFrontend.isEditMode() (the exact inverse of that file's own
 * guard), so exactly one of the two files ever does real work for a given
 * page view.
 *
 * By default (Editing Mode off), this behaves like a normal live carousel
 * so the editor preview matches the real frontend. Turning "Editing Mode"
 * on freezes the carousel on one slide (touch/keyboard/mousewheel
 * disabled) and lets the "Active Slide" dropdown jump directly to any
 * slide via Swiper's own slideTo() — replacing drag-to-browse while
 * editing content, per explicit user request.
 *
 * Settings are read from Elementor's own in-memory editor model data
 * (`elementorFrontend.config.elements.data[modelCID].attributes`), NOT
 * from a `data-settings` DOM attribute — confirmed by reading the actual
 * `getElementSettings()` implementation Elementor's Handler base class
 * uses (elementor/assets/js/frontend-modules.js): in edit mode it reads
 * `elementorFrontend.config.elements.data[modelCID]`, keyed by the
 * widget's own `data-model-cid` attribute; `data-settings` is only used
 * in its non-edit (live frontend) branch, and — confirmed empirically, by
 * inspecting the actual rendered editor-preview HTML — is never present
 * on the wrapper element in the editor at all. This is also why there is
 * no `elementorFrontend.getElementSettings()` global — that method only
 * exists on the Handler base class itself (`this.$element`/`this.isEdit`-
 * aware), not as a standalone global; calling it as if it were one threw
 * `getElementSettings is not a function`. This vanilla script replicates
 * just the edit-mode branch of that same logic instead (skipping the
 * Handler class's `getActiveControls()`/`frontend_available` key
 * filtering, since we only ever read a few specific, known keys directly).
 *
 * Editor-only simplification: unlike the live frontend, this does not
 * build a responsive Swiper `breakpoints` object — always uses the
 * desktop-tier Slides Per View/Space Between/Centered Slides values. A
 * bounded editor-preview fidelity gap, consistent with the already-
 * accepted device-preview-toggle limitation on this and other carousel
 * widgets.
 */
( function () {
	'use strict';

	function getElementSettings( element ) {
		var modelCid = element.getAttribute( 'data-model-cid' );
		var elementsData = window.elementorFrontend.config && window.elementorFrontend.config.elements && window.elementorFrontend.config.elements.data;
		var elementData = modelCid && elementsData ? elementsData[ modelCid ] : null;

		if ( elementData && elementData.attributes ) {
			return elementData.attributes;
		}

		// Fallback for when the model data isn't reachable this way (e.g.
		// Elementor's internal config shape changes) — matches the live-
		// frontend branch of Elementor's own Handler base class, though
		// data-settings is not expected to be present in the editor.
		try {
			return JSON.parse( element.getAttribute( 'data-settings' ) || '{}' );
		} catch ( e ) {
			return {};
		}
	}

	function buildEditorSwiperOptions( settings, isEditing ) {
		var slideCount = ( settings.hero_slides && settings.hero_slides.length ) || 0;
		var isSingleSlide = 1 === slideCount;

		var effect = settings.effect || 'slide';

		var options = {
			// Forced off in the editor regardless of widget settings, even
			// with Editing Mode off — avoids Swiper's loop-mode slide-
			// cloning DOM mutation fighting with Elementor's own repeater
			// DOM mutations, same precedent Elementor Pro's own Nested
			// Carousel widget follows in its editor-mode settings. Do NOT
			// add `observer`/`observeParents` here instead — this project
			// already hit and reverted a documented observer + loop
			// feedback-loop bug on the youtube-feed widget.
			loop: false,
			// loop is always forced off above, so rewind just needs its own
			// setting checked (no need to also guard on loop being off).
			rewind: ! isSingleSlide && 'yes' === settings.rewind,
			speed: parseInt( settings.transition_speed, 10 ) || 0,
			effect: effect,
			// slides_per_view is a SLIDER control (see
			// register_carousel_settings_controls() in
			// trait-gws-elementor-swiper-carousel.php), so Elementor's raw
			// settings model stores it as `{ size, unit }`, not a plain
			// number - parseFloat() on that object directly coerces to the
			// string "[object Object]" first, giving NaN -> the `|| 1`
			// fallback, which is why the editor preview was always showing
			// 1 slide per view (full width) regardless of the widget's
			// real Slides Per View setting. The live frontend never hit
			// this: PHP already extracts `.size` before printing it as a
			// plain-number `data-gws-carousel-slides-per-view` DOM
			// attribute (get_carousel_render_data() in the same trait),
			// which elementor-swiper-carousel.js then just parses as-is -
			// this file is the one path that reads Elementor's raw JS
			// settings model directly instead, since the editor preview
			// has no PHP round-trip to do that extraction for it.
			slidesPerView: parseFloat( settings.slides_per_view && settings.slides_per_view.size ) || 1,
			slidesPerGroup: parseInt( settings.slides_per_group, 10 ) || 1,
			initialSlide: parseInt( settings.initial_slide, 10 ) || 0,
			spaceBetween: parseInt( settings.space_between, 10 ) || 0,
			centeredSlides: ! isSingleSlide && 'yes' === settings.centered_slides,
			direction: settings.direction || 'horizontal',
			grabCursor: ! isSingleSlide && 'yes' === settings.grab_cursor,
		};

		if ( 'fade' === effect ) {
			options.fadeEffect = { crossFade: true };
		}

		// Autoplay only suppressed while Editing Mode is actually on (an
		// auto-advancing carousel while trying to edit its content would
		// fight the very thing Editing Mode exists to prevent) — with
		// Editing Mode off, the editor preview should behave like the real
		// frontend, autoplay included.
		var autoplayDelay = parseInt( settings.autoplay_delay, 10 ) || 0;

		if ( ! isEditing && ! isSingleSlide && 'yes' === settings.autoplay && autoplayDelay > 0 ) {
			options.autoplay = {
				delay: autoplayDelay,
				disableOnInteraction: false,
				pauseOnMouseEnter: 'yes' === settings.pause_on_hover,
				// Same fix as elementor-swiper-carousel.js's shared builder
				// (see that file's own comment) - Swiper's own default here
				// is false, meaning autoplay restarts from slide 1 once it
				// reaches the end even with loop/rewind both off. `loop` is
				// always false in this editor-preview branch (see this
				// file's own top docblock), so this simplifies to just
				// `!options.rewind`, but written the same way as the shared
				// builder for consistency/in case that assumption ever
				// changes.
				stopOnLastSlide: ! options.loop && ! options.rewind,
			};
		}

		if ( ! isSingleSlide && 'yes' === settings.show_arrows ) {
			// Custom nav_prev_selector/nav_next_selector overrides (see
			// register_carousel_nav_controls() in
			// trait-gws-elementor-swiper-carousel.php) — a FULL selector
			// (including the # or . sigil) as entered by the user, since
			// Elementor Core only guarantees a CSS ID field, not CSS
			// Classes, so we can't assume one or the other and prepend a
			// dot ourselves. Resolved via `document.querySelector` (not
			// handed to Swiper as a bare string) so a target OUTSIDE this
			// widget's own DOM — e.g. a Button widget placed elsewhere on
			// the page — is found reliably, matching
			// elementor-swiper-carousel.js's approach exactly rather than
			// relying on Swiper's own internal selector-resolution scoping
			// rules.
			options.navigation = {
				nextEl: settings.nav_next_selector ? document.querySelector( settings.nav_next_selector ) : '.gws-carousel-arrow-next',
				prevEl: settings.nav_prev_selector ? document.querySelector( settings.nav_prev_selector ) : '.gws-carousel-arrow-prev',
			};
		}

		if ( ! isSingleSlide && 'yes' === settings.show_pagination ) {
			var paginationEl = settings.nav_pagination_selector ? document.querySelector( settings.nav_pagination_selector ) : null;

			// A custom target element needs this class added so the
			// widget's own Dot Size/Spacing/Color style controls still
			// apply to it (same reasoning as elementor-swiper-carousel.js)
			// — a no-op on the built-in element, which already has it.
			if ( paginationEl ) {
				paginationEl.classList.add( 'gws-carousel-pagination' );
			}

			options.pagination = { el: paginationEl || '.gws-carousel-pagination', clickable: true };
		}

		if ( ! isSingleSlide && 'yes' === settings.keyboard ) {
			options.keyboard = { enabled: true };
		}

		if ( ! isSingleSlide && 'yes' === settings.mousewheel ) {
			options.mousewheel = { enabled: true };
		}

		return options;
	}

	function applyEditingMode( swiper, hero, settings ) {
		var isEditing = 'yes' === settings.editing_mode;

		hero.classList.toggle( 'gws-hero--editing-mode', isEditing );

		if ( isEditing ) {
			swiper.allowTouchMove = false;

			if ( swiper.keyboard ) {
				swiper.keyboard.disable();
			}

			if ( swiper.mousewheel ) {
				swiper.mousewheel.disable();
			}

			var activeIndex = parseInt( settings.active_slide, 10 ) || 0;
			swiper.slideTo( activeIndex, 0 );
		} else {
			swiper.allowTouchMove = true;

			if ( swiper.keyboard && 'yes' === settings.keyboard ) {
				swiper.keyboard.enable();
			}

			if ( swiper.mousewheel && 'yes' === settings.mousewheel ) {
				swiper.mousewheel.enable();
			}
		}
	}

	function registerElementReadyHook() {
		window.elementorFrontend.hooks.addAction( 'frontend/element_ready/gws-slider.default', function ( $scope ) {
			if ( ! window.elementorFrontend.isEditMode || ! window.elementorFrontend.isEditMode() ) {
				return;
			}

			var hero = $scope[ 0 ].querySelector( '.gws-hero--carousel' );

			if ( ! hero ) {
				return;
			}

			var carousel = hero.querySelector( '.swiper' );

			if ( ! carousel ) {
				return;
			}

			// Always fully destroy and recreate rather than patching a live
			// instance — Swiper caches its slide list at init time and has
			// no idea Elementor's own repeater editing just mutated the
			// DOM out from under it, so a fresh instance is the safest way
			// to stay in sync with whatever the panel/repeater last did.
			if ( carousel.swiper ) {
				carousel.swiper.destroy( true, true );
			}

			var settings = getElementSettings( $scope[ 0 ] );
			var isEditing = 'yes' === settings.editing_mode;
			var swiper = new window.Swiper( carousel, buildEditorSwiperOptions( settings, isEditing ) );

			applyEditingMode( swiper, hero, settings );

			// Same fix as elementor-slider.js's live-frontend branch (see
			// that file's own comment for the full explanation) - the
			// editor preview renders from the same server-side
			// render_carousel_slides() padding logic on initial load, so
			// it's expected to hit the identical issue once real Slides
			// Per View exceeds the number of authored slides.
			if ( window.GWSInitCardSizer ) {
				hero.querySelectorAll( '.gws-card' ).forEach( function ( card ) {
					window.GWSInitCardSizer( card );
				} );
			}
		} );
	}

	if ( window.elementorFrontend && window.elementorFrontend.hooks ) {
		registerElementReadyHook();
	} else {
		window.addEventListener( 'elementor/frontend/init', registerElementReadyHook );
	}
} )();
